import { v4 as uuidv4 } from 'uuid';
import useFeedbackStore from './FeedbackStore';

/**
 * Message display types - matches backend MessageType enum
 */
export const MESSAGE_TYPE = {
  PANEL: 'panel',        // FeedbackPanel history log (default)
  CONTEXTUAL: 'contextual', // Floating message at click position
  BOTH: 'both',          // Both panel and contextual
  CUSTOM: 'custom'       // Message under element with specified CSS class
};

/**
 * Default duration for contextual messages in milliseconds
 */
const DEFAULT_CONTEXTUAL_DURATION = 2000;

/**
 * Un Set para rastrear los mensajes que ya se han mostrado con '...Once'.
 * Esto previene que el mismo mensaje "Once" aparezca repetidamente.
 */
const seenMessages = new Set();

/**
 * Routes a message to the appropriate display system based on messageType
 * @param {string} type - The message type (success, error, etc.)
 * @param {string} message - The message content
 * @param {object} options - Additional options including messageType and messageOptions
 */
function routeMessage(type, message, options = {}) {
  const {
    messageType = MESSAGE_TYPE.PANEL, // Default to panel for backward compatibility
    messageOptions = {},
    ...restOptions
  } = options;

  const generatedId = uuidv4();
  const baseMessage = {
    id: generatedId,
    message,
    type,
    time: new Date().toLocaleTimeString(),
    ...restOptions
  };

  const store = useFeedbackStore.getState();

  switch (messageType) {
    case MESSAGE_TYPE.CONTEXTUAL:
      addContextualMessage(store, baseMessage, messageOptions);
      break;

    case MESSAGE_TYPE.BOTH:
      store.addFeedback(baseMessage);
      addContextualMessage(store, baseMessage, messageOptions);
      break;

    case MESSAGE_TYPE.CUSTOM:
      // Custom messages use a CSS class selector for positioning
      // The customClass option specifies which element to appear under
      addContextualMessage(store, baseMessage, {
        ...messageOptions,
        useCustomClass: true
      });
      break;

    case MESSAGE_TYPE.PANEL:
    default:
      store.addFeedback(baseMessage);
      break;
  }
}

/**
 * Adds a contextual message and schedules its auto-removal
 * @param {object} store - FeedbackStore instance
 * @param {object} baseMessage - The base message object
 * @param {object} messageOptions - Options like duration, customClass
 */
function addContextualMessage(store, baseMessage, messageOptions = {}) {
  const duration = messageOptions.duration || DEFAULT_CONTEXTUAL_DURATION;

  const contextualMsg = {
    ...baseMessage,
    duration,
    customClass: messageOptions.customClass || null,
    useCustomClass: messageOptions.useCustomClass || false
  };

  store.addContextualMessage(contextualMsg);

  // Schedule auto-removal after duration
  setTimeout(() => {
    useFeedbackStore.getState().removeContextualMessage(baseMessage.id);
  }, duration);
}

/**
 * Legacy function for backward compatibility - adds to panel only
 * @param {string} type - The message type (success, error, etc.)
 * @param {string} message - The message content
 * @param {object} options - Additional options
 */
function addMessage(type, message, options = {}) {
  routeMessage(type, message, { ...options, messageType: MESSAGE_TYPE.PANEL });
}

/**
 * Public feedback functions
 * All functions now support messageType and messageOptions in the options object
 */
export function success(message, options) {
  routeMessage('success', message, options);
}

export function error(message, options) {
  routeMessage('error', message, options);
}

export function warning(message, options) {
  routeMessage('warning', message, options);
}

export function info(message, options) {
  routeMessage('info', message, options);
}

export function log(message, options) {
  routeMessage('log', message, options);
}

/**
 * Show a contextual message at the last click position
 * Convenience function for showing contextual-only messages
 * @param {string} message - The message to display
 * @param {object} options - Options like duration
 */
export function contextual(message, options = {}) {
  routeMessage('info', message, {
    ...options,
    messageType: MESSAGE_TYPE.CONTEXTUAL
  });
}

/**
 * Limpia todos los mensajes del historial.
 */
export function clear() {
  useFeedbackStore.getState().clearFeedback();
  seenMessages.clear(); // También limpiamos los mensajes vistos
}

/**
 * Muestra un mensaje de éxito, pero solo una vez por sesión para el mismo mensaje.
 */
export function successOnce(message, options) {
  if (!seenMessages.has(message)) {
    seenMessages.add(message);
    success(message, options);
  }
}

/**
 * Muestra un mensaje de error, pero solo una vez por sesión.
 */
export function errorOnce(message, options) {
  if (!seenMessages.has(message)) {
    seenMessages.add(message);
    error(message, options);
  }
}

/**
 * Muestra una advertencia, pero solo una vez por sesión.
 */
export function warningOnce(message, options) {
  if (!seenMessages.has(message)) {
    seenMessages.add(message);
    warning(message, options);
  }
}

/**
 * Muestra un mensaje de información, pero solo una vez por sesión.
 */
export function infoOnce(message, options) {
  if (!seenMessages.has(message)) {
    seenMessages.add(message);
    info(message, options);
  }
}

/**
 * Muestra un log, pero solo una vez por sesión.
 */
export function logOnce(message, options) {
  if (!seenMessages.has(message)) {
    seenMessages.add(message);
    log(message, options);
  }
}

// Exportamos un objeto que contiene todas las funciones para un uso conveniente.
const feedback = {
  success,
  error,
  warning,
  info,
  log,
  clear,
  successOnce,
  errorOnce,
  warningOnce,
  infoOnce,
  logOnce,
  contextual,
  MESSAGE_TYPE,
};

export default feedback;
